export function isSpeechToText(model: string): boolean {
  const modelLower = model.toLowerCase();
  return modelLower.includes("whisper") || modelLower.includes("sautech");
}

export function isImageToText(model: string): boolean {
  return model.toLowerCase().includes("llava");
}

export function isTextToText(model: string): boolean {
  return !(isSpeechToText(model) || isImageToText(model));
}

export function isTextToSpeech(model: string): boolean {
  return model.toLowerCase().includes("tts");
}

export const modelSupportsImageInMessages = (model: string): boolean => {
  return (
    (model.includes("llama") && model.includes("vision")) ||
    model.includes("quattro") ||
    model.includes("llama-4") ||
    model.includes("llama-guard-4")
  );
};

export const modelDoesNotSupportSystemPromptIfImageInMessages = (
  model: string,
): boolean => {
  return model.includes("llama") && model.includes("vision");
};

export const modelShouldBeHidden = (model: string): boolean => {
  return model.includes("3.1") && !model.includes("llama-3.1-8b-instant");
};

export const isPreviewModel = (model: string): boolean => {
  const previewModels = [
    "mock-llama3-model",
    "llama-3.1-8b-instant",
    "llama-3.1-70b-versatile",
    "llama-3.1-405b-reasoning",
  ];
  return previewModels.includes(model);
};

const thinkingRegex = /[\s\S]*<think>([\s\S]*?)<\/think>/i;

export function splitThinkingContent(text: string): [string, string] {
  // Regular expression to match the <thinking> content
  const thinkingMatch = text.match(thinkingRegex);

  let thinkingText = "";
  let outsideText = text;

  if (thinkingMatch) {
    // Extract the thinking text
    thinkingText = thinkingMatch[1]?.trim() || "";

    // Remove the thinking section from the outside text
    outsideText = text.replace(thinkingMatch[0], "").trim();
  } else {
    // Handle case where <thinking> is open but not closed
    const openThinkingIndex = text.toLowerCase().indexOf("<think>");

    if (openThinkingIndex !== -1) {
      thinkingText = text.slice(openThinkingIndex + 8).trim();
      outsideText = text.slice(0, openThinkingIndex).trim();
    }
  }

  return [thinkingText, outsideText];
}

// Basic RTL language detection based on character codes
export const isRtlText = (text: string) => {
  if (typeof text !== "string") {
    return;
  }
  const rtlChars =
    /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
  const rtlDirCharsCount = (text.match(rtlChars) || []).length;

  // This is a very basic check and might consider a text as RTL even if it contains a mix of RTL and LTR characters
  return rtlDirCharsCount > text.length - rtlDirCharsCount;
};

export function isSpecialParamsModel(model: string) {
  return model.includes("deepseek") || model.includes("qwen");
}

export function isCompoundSystem(model: string): boolean {
  return model.toLowerCase().includes("compound");
}

/**
 * Filters out <tool>, </tool>, <output>, and </output> blocks from reasoning text.
 * Also handles partial content where a tag starts but doesn't close.
 * @param text The input text to filter
 * @returns The filtered text with tool and output blocks removed
 */
export function filterToolOutputBlocks(text: string): string {
  if (!text) {
    return "";
  }

  // Replace complete tool/output blocks with nothing
  let filteredText = text;

  // Remove complete <tool>...</tool> blocks
  filteredText = filteredText.replace(/<tool>[\s\S]*?<\/tool>/g, "");

  // Remove complete <output>...</output> blocks
  filteredText = filteredText.replace(/<output>[\s\S]*?<\/output>/g, "");

  // Handle partial tags - remove everything after <tool> or <output> if not closed
  const toolIndex = filteredText.indexOf("<tool>");
  if (toolIndex !== -1) {
    filteredText = filteredText.substring(0, toolIndex);
  }

  const outputIndex = filteredText.indexOf("<output>");
  if (outputIndex !== -1) {
    filteredText = filteredText.substring(0, outputIndex);
  }
  return filteredText.trim();
}
